import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { useAddEmail } from '../../../hooks/apolloHooks/useAddEmail';
import { useDeleteEmail } from '../../../hooks/apolloHooks/useDeleteEmail';
import { useGetCurrentUserProfile } from '../../../hooks/apolloHooks/useGetCurrentUserProfile';
import { useGetCustomer } from '../../../hooks/apolloHooks/useGetCustomer';
import { useGetDeliveryTypes } from '../../../hooks/apolloHooks/useGetDeliveryTypes';
import { useGetOwnerCustomerRoles } from '../../../hooks/apolloHooks/useGetOwnerCustomerRoles';
import { StatementMailingDeliveryType, useUpdateStatementDelivery } from '../../../hooks/apolloHooks/useUpdateStatementDelivery';
import { GET_BASIC_CUSTOMER_DATA_QUERY, GET_CARD_STATEMENTS_QUERY, GET_CUSTOMER_RIGHTS_DATA_QUERY } from '../../../integrations/allkort/graphql/gql/CustomerQueries';
import { BasicCustomerDataResponse, CardStatementsCustomerDataResponse, ContactDataResponse, CustomerRightsDataResponse } from '../../../integrations/allkort/graphql/interface/Customer';
import { EmailTypeId } from '../../../integrations/allkort/graphql/interface/Emails';
import { validateEmail } from '../../../utils/contactValidationSchemas';
import { EMAIL_TYPES } from '../../../utils/filterEmailsByType';
import { ArrowRightIcon, CloseIcon, InfoIconOutlined, PenIcon, SwitchArrowsIcon } from '../../svgs/ExpressonSVGs';
import Button from '../Button';
import Link from '../Link';
import RightsWrapper from '../RightsWrapper';
import Tooltip from '../Tooltip';
import VerifyEmail from '../VerifyEmail';
import CardEmailDelivery from './CardEmailDelivery';
import CardLetterDelivery from './CardLetterDelivery';

type ProfileSmartCardProps = {
    wrapperClasses?: string;
    customerContactData: ContactDataResponse;
    statementEmailType?: EmailTypeId;
    refetchContactData?: () => void;
}

type DraftEmail =
    | { kind: 'existing'; id: number; address: string; verified: boolean }
    | { kind: 'new'; address: string }

const ProfileSmartCard: React.FC<ProfileSmartCardProps> = ({ wrapperClasses = '', customerContactData, statementEmailType = 'INVOICE_STATEMENT', refetchContactData }) => {
    const [isManageMode, setIsManageMode] = useState(false);
    const [pendingDeliveryType, setPendingDeliveryType] = useState<StatementMailingDeliveryType | null>(null);
    const [draftEmails, setDraftEmails] = useState<DraftEmail[]>([]);
    const [newEmailInput, setNewEmailInput] = useState('');
    const [newEmailError, setNewEmailError] = useState<string | undefined>(undefined);
    const [isNewEmailValid, setIsNewEmailValid] = useState(false);
    const [isAddingEmail, setIsAddingEmail] = useState(false);
    const [pendingVerification, setPendingVerification] = useState<{ email: string; emailId: number } | null>(null);
    const [isEmailVerifying, setIsEmailVerifying] = useState(false);
    const { invoiceDelivery, statementDelivery, loading, refetch: refetchDeliveryTypes } = useGetDeliveryTypes();
    // invoiceDelivery?.deliveryType.name  → "E-postutskick"
    // statementDelivery                   → null (if not set)

    const { updateStatementDelivery, loading: updateStatementDeliveryLoading, error: updateStatementDeliveryError, data: updateStatementDeliveryData } = useUpdateStatementDelivery();
    const { addEmail } = useAddEmail();
    const { deleteEmail } = useDeleteEmail();

    const { data: customerBasicData } = useGetCustomer<BasicCustomerDataResponse>(
        GET_BASIC_CUSTOMER_DATA_QUERY,
        undefined,
        { fetchPolicy: 'cache-first' }
    );
    const customerId = customerBasicData?.currentCustomer?.id ?? null;

    const { data: customerRightsData } = useGetCustomer<CustomerRightsDataResponse>(
        GET_CUSTOMER_RIGHTS_DATA_QUERY,
        {},
        { fetchPolicy: 'network-only' }
    );
    const isFrozen = customerRightsData?.currentCustomer?.frozen?.is ?? false;
    const hasBlockingState = (customerRightsData?.currentCustomer?.state ?? null) !== null;

    const { userProfile } = useGetCurrentUserProfile();
    const { roles: availableRoles, isComplete: rolesComplete } = useGetOwnerCustomerRoles();
    const availableWriteClaimNames = rolesComplete
        ? availableRoles.filter(r => r.claimName.includes('mutation')).map(r => r.claimName)
        : [];
    const isSuperUser = (userProfile?.roles ?? []).some(r => r.claimName === 'Superuser');
    const hasWriteRights = isSuperUser || (availableWriteClaimNames.length > 0 &&
        availableWriteClaimNames.every(name => (userProfile?.roles ?? []).some(r => r.claimName === name)));

    const canInteract = !isFrozen && !hasBlockingState && hasWriteRights;
    const { data: statementsExistData } = useGetCustomer<CardStatementsCustomerDataResponse>(
        GET_CARD_STATEMENTS_QUERY,
        { first: null, after: null, before: null, last: 1 },
        { fetchPolicy: 'cache-first' }
    );
    const hasStatements = (statementsExistData?.currentCustomer?.prepaidStatements?.nodes?.length ?? 0) > 0;

    const handleUpdateStatementDelivery = async (customerId: number, deliveryType: StatementMailingDeliveryType) => {
        try {
            await updateStatementDelivery({ customerId, deliveryType });
        } catch (err) {
            console.error('Failed to update statement delivery:', err);
        }
    };

    const dialogRef = useRef<HTMLDialogElement>(null);

    const statementEmails = (customerContactData?.currentCustomer?.emails || [])
        .filter(e => e.type.id === EMAIL_TYPES[statementEmailType as keyof typeof EMAIL_TYPES]);

    const openDialog = (deliveryType: StatementMailingDeliveryType) => {
        if (!canInteract) return;
        const initial: DraftEmail[] = deliveryType === StatementMailingDeliveryType.EMAIL_DISPATCH
            ? statementEmails.map(e => ({ kind: 'existing', id: e.id, address: e.address, verified: e.verified }))
            : [];
        setDraftEmails(initial);
        setNewEmailInput('');
        setNewEmailError(undefined);
        setIsNewEmailValid(false);
        setIsManageMode(false);
        setPendingVerification(null);
        setPendingDeliveryType(deliveryType);
        dialogRef.current?.showModal();
    };

    const openManageDialog = () => {
        if (!canInteract) return;
        const initial = statementEmails.map(e => ({ kind: 'existing' as const, id: e.id, address: e.address, verified: e.verified }));
        setDraftEmails(initial);
        setNewEmailInput('');
        setNewEmailError(undefined);
        setIsNewEmailValid(false);
        setIsManageMode(true);
        setPendingVerification(null);
        setPendingDeliveryType(StatementMailingDeliveryType.EMAIL_DISPATCH);
        dialogRef.current?.showModal();
    };

    const handleNewEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNewEmailInput(value);
        if (value.length === 0) {
            setNewEmailError(undefined);
            setIsNewEmailValid(false);
            return;
        }
        const result = validateEmail(value);
        setIsNewEmailValid(result.valid);
        setNewEmailError(result.valid ? undefined : result.error);
    };

    const handleAddDraftEmail = async () => {
        const trimmed = newEmailInput.trim();
        const result = validateEmail(trimmed);
        if (!result.valid || draftEmails.some(e => e.address === trimmed) || !customerId) return;
        setIsAddingEmail(true);
        try {
            const added = await addEmail({ address: trimmed, customerId, emailType: statementEmailType });
            const emailId = added?.[0]?.id;
            if (emailId) {
                setPendingVerification({ email: trimmed, emailId });
                setNewEmailInput('');
                setNewEmailError(undefined);
                setIsNewEmailValid(false);
            }
        } catch (err) {
            console.error('[ProfileSmartCard] addEmail failed:', err);
        } finally {
            setIsAddingEmail(false);
        }
    };

    const removeDraftEmail = (index: number) => {
        const email = draftEmails[index];
        setDraftEmails(prev => prev.filter((_, i) => i !== index));
        if (email.kind === 'existing') {
            deleteEmail({ emailId: email.id }).catch(err =>
                console.error('[ProfileSmartCard] deleteEmail failed:', err)
            );
        }
    };

    const handleConfirmDeliveryChange = async () => {
        if (!pendingDeliveryType || !customerId) return;
        // Email adds/removes are already instant; only switch delivery type here
        if (!isManageMode) {
            await handleUpdateStatementDelivery(customerId, pendingDeliveryType);
        }
        dialogRef.current?.close();
        setPendingDeliveryType(null);
        setIsManageMode(false);
        refetchDeliveryTypes();
        refetchContactData?.();
    };

    const handleCancel = async () => {
        if (pendingVerification) {
            await deleteEmail({ emailId: pendingVerification.emailId });
            setPendingVerification(null);
        }
        const hasNewAdditions = draftEmails.some(e => e.kind === 'existing' && !originalEmailIds.has(e.id));
        if (isManageMode && (hasEmailChanges || hasNewAdditions)) {
            refetchContactData?.();
        }

        const shouldFallbackToPaper = pendingDeliveryType === StatementMailingDeliveryType.EMAIL_DISPATCH && draftEmails.length === 0;
        if (shouldFallbackToPaper && customerId) {
            await handleUpdateStatementDelivery(customerId, StatementMailingDeliveryType.PAPER_DISPATCH);
            refetchDeliveryTypes();
        }
        dialogRef.current?.close();
        setPendingDeliveryType(null);
        setIsManageMode(false);
    };

    const originalEmailIds = new Set(statementEmails.map(e => e.id));
    const remainingEmailIds = new Set(
        draftEmails.filter((e): e is Extract<DraftEmail, { kind: 'existing' }> => e.kind === 'existing').map(e => e.id)
    );
    // Only pending deletions matter — adds are already persisted via addEmail+verify
    const hasEmailChanges = [...originalEmailIds].some(id => !remainingEmailIds.has(id));
    const hasVerifiedEmail = draftEmails.some(e => e.kind === 'existing' && e.verified);

    return (
        <div className={classNames(
            'profile-settings-smart-card',
            'profilesettingssmartcard_component',
            'white-pale-section',
            'shadow-one',
            'with-header',
            wrapperClasses
        )}>
            <div className="header">
                <h2 className="body-text">
                    <span className="smartcard-icon card-icon">S</span>
                    Smartkort
                    <Tooltip text="Förutom valt leveranssätt så kan du alltid se dina kontobesked digitalt på Mina sidor." position="top-end">
                        <InfoIconOutlined width="26" height="26" />
                    </Tooltip>
                </h2>
            </div>

            {statementDelivery !== null ? (
                <div className="content">
                    <h5 className="body-text">
                        Kontobesked
                        {statementDelivery?.deliveryType.id === 4 && (
                            <RightsWrapper
                                disableButtonText='Hantera e-postadresser'
                                disableButtonIcon={<PenIcon width={18} height={18} />}
                                disableButtonClasses={classNames('body-text')}
                            >
                                <Link
                                    href={'#'}
                                    className="edit-action"
                                    onClick={() => openManageDialog()}
                                >
                                    <PenIcon width={18} height={18} />
                                    Hantera e-postadresser
                                </Link>
                            </RightsWrapper>
                        )}
                    </h5>


                    <p>
                        Välj hur du vill ta emot dina månatliga kontobesked för dina smartkort.
                    </p>

                    <div className="delivery-methods">
                        <CardEmailDelivery
                            cardType="P"
                            isActiveMethod={statementDelivery?.deliveryType.id === 4}
                            customerContactData={customerContactData}
                            onSelect={() => openDialog(StatementMailingDeliveryType.EMAIL_DISPATCH)}
                        />

                        <CardLetterDelivery
                            cardType="P"
                            isActiveMethod={statementDelivery?.deliveryType.id === 1}
                            customerContactData={customerContactData}
                            onSelect={() => openDialog(StatementMailingDeliveryType.PAPER_DISPATCH)}
                        />
                    </div>
                    {hasStatements && (
                        <div className="delivery-methods-info">
                            <Link
                                ariaLabel={'Visa kontobesked'}
                                href={'/mitt-konto/fakturor-kontobesked#statements'}
                            >
                                Visa senaste kontobeskedet
                                <ArrowRightIcon width={22} height={22} />
                            </Link>
                        </div>
                    )}
                </div>
            ) : (
                <div className="content">
                    <h5 className="body-text">
                        Kontobesked
                    </h5>
                    <p>
                        Något gick fel kontakta <Link className="link" href="/kontakt">kundservice</Link> för att ändra leveranssätt på dina kontobesked.
                    </p>
                </div>
            )}

            <dialog
                ref={dialogRef}
                className="edit-delivery-method-dialog"
                onCancel={handleCancel}
            >
                <div className="card-delivery__dialog-content">
                    <h2 className="card-delivery__title body-text">
                        <span className="svg-icon-wrapper orange round">
                            <SwitchArrowsIcon width={28} height={28} />
                        </span>
                        {isManageMode ? 'Hantera e-postadresser' : 'Byt leveranssätt'}
                    </h2>
                    <p>
                        {isManageMode
                            ? invoiceDelivery?.deliveryType.id === 4
                                ? 'Hantera vilka e-postadresser som får dina kontobesked och fakturor.'
                                : 'Hantera vilka e-postadresser som får dina kontobesked.'
                            : pendingDeliveryType === StatementMailingDeliveryType.EMAIL_DISPATCH
                                ? invoiceDelivery?.deliveryType.id === 4
                                    ? 'Dina kontobesked och fakturor kommer att skickas via e-post. Välj vilka e-postadresser du vill använda nedan.'
                                    : 'Dina kontobesked kommer att skickas via e-post. Välj vilka e-postadresser du vill använda nedan.'
                                : 'Dina kontobesked kommer att skickas via brev till din folkbokföringsadress.'}
                    </p>

                    {(isManageMode || pendingDeliveryType === StatementMailingDeliveryType.EMAIL_DISPATCH) && (
                        <div className="select-emails">
                            <div className="add-email">
                                <div>
                                    <input
                                        type="email"
                                        placeholder="Lägg till e-postadress"
                                        name="newStatementEmail"
                                        value={newEmailInput}
                                        onChange={handleNewEmailChange}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddDraftEmail(); } }}
                                    />
                                    <Button
                                        type="button"
                                        className="add-email-button body-text"
                                        disabled={!isNewEmailValid || !!pendingVerification || isAddingEmail}
                                        onClick={handleAddDraftEmail}
                                    >
                                        Lägg till
                                    </Button>
                                </div>
                                {newEmailError && newEmailInput.length > 0 && (
                                    <span className="input-error">{newEmailError}</span>
                                )}
                            </div>
                            {draftEmails.length > 0 && (
                                <div className="receipt-emails__list">
                                    {draftEmails.map((email, index) => (
                                        <div
                                            key={email.kind === 'existing' ? email.id : `new-${email.address}`}
                                            className={classNames('receipt-email', {
                                                'receipt-email--new': email.kind === 'new',
                                                'receipt-email--unverified': email.kind === 'existing' && !email.verified,
                                            })}
                                        >
                                            <span
                                                className="receipt-email__address"
                                                onClick={() => removeDraftEmail(index)}
                                            >
                                                {email.address}
                                                <CloseIcon width={24} height={24} />
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {draftEmails.some(email => email.kind === 'existing' && !email.verified) && (
                                <p className="input-error">Vissa e-postadresser är inte verifierade. De är markerade i rött, vänligen ta bort dem och lägg till igen för att gå igenom verifieringsprocessen.</p>
                            )}

                            {/* Loader to indicate email is being added */}
                            {isAddingEmail && (
                                <span className="loader-zoom"></span>
                            )}

                            {/* Pending verification */}
                            {pendingVerification && (
                                <VerifyEmail
                                    email={pendingVerification.email}
                                    emailId={pendingVerification.emailId}
                                    onVerifySuccess={() => setIsEmailVerifying(true)}
                                    onVerified={(verifiedEmail) => {
                                        const emailId = pendingVerification!.emailId;
                                        setDraftEmails(prev => [...prev, { kind: 'existing', id: emailId, address: verifiedEmail, verified: true }]);
                                        setPendingVerification(null);
                                        setIsEmailVerifying(false);
                                    }}
                                />
                            )}
                        </div>
                    )}

                    <div className="card-delivery__actions">
                        {!isManageMode && (
                            <Button
                                className="body-text"
                                type="button"
                                onClick={handleConfirmDeliveryChange}
                                disabled={updateStatementDeliveryLoading || !!pendingVerification || (pendingDeliveryType === StatementMailingDeliveryType.EMAIL_DISPATCH && (draftEmails.length === 0 || !hasVerifiedEmail))}
                            >
                                {updateStatementDeliveryLoading ? 'Sparar...' : 'Byt leveranssätt'}
                            </Button>
                        )}
                        {pendingDeliveryType === StatementMailingDeliveryType.EMAIL_DISPATCH && statementDelivery?.deliveryType.id !== 1 && draftEmails.length === 0 && (
                            <span className="input-info">
                                Väljer du ingen e-postadress ändras leveranssättet till brev.
                            </span>
                        )}
                        <Button
                            type="button"
                            onClick={handleCancel}
                            className="button-cancel body-text"
                            disabled={updateStatementDeliveryLoading || isEmailVerifying}
                        >
                            {isManageMode ? 'Tillbaka' : 'Avbryt'}
                        </Button>
                    </div>
                </div>
            </dialog>
        </div>
    )
}

export default ProfileSmartCard;